import React, { useState, useEffect } from 'react';
import { Plus, CheckCircle, Clock, AlertCircle, Calendar, User, Trash2, Edit } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AgentTasks = ({ agent }) => {
  const { toast } = useToast();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [newTask, setNewTask] = useState({
    userId: '',
    title: '',
    description: '',
    taskType: 'follow_up',
    priority: 'medium',
    dueDate: ''
  });

  useEffect(() => {
    if (agent?.agentId) {
      fetchTasks();
      fetchClients();
    }
  }, [agent]);

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/tasks/${agent.agentId}`);
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/clients/${agent.agentId}`);
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newTask,
          agentId: agent.agentId
        })
      });

      if (response.ok) {
        toast({ title: "Task created successfully" });
        setShowCreateModal(false);
        setNewTask({
          userId: '',
          title: '',
          description: '',
          taskType: 'follow_up',
          priority: 'medium',
          dueDate: ''
        });
        fetchTasks();
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Failed to create task" });
    }
  };

  const handleToggleStatus = async (taskId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
      const response = await fetch(`${BACKEND_URL}/api/agent/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast({ title: `Task marked as ${newStatus}` });
        fetchTasks();
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Failed to update task" });
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/tasks/${taskId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({ title: "Task deleted successfully" });
        fetchTasks();
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Failed to delete task" });
    }
  };

  const getPriorityBadge = (priority) => {
    const config = {
      'low': { bg: 'bg-slate-100', text: 'text-slate-700' },
      'medium': { bg: 'bg-blue-100', text: 'text-blue-700' },
      'high': { bg: 'bg-red-100', text: 'text-red-700' }
    };
    const c = config[priority] || config.medium;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${c.bg} ${c.text}`}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </span>
    );
  };

  const filteredTasks = filterStatus === 'all' ? tasks : tasks.filter(t => t.status === filterStatus);

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Tasks</h2>
          <p className="text-slate-600">{filteredTasks.length} tasks</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-slate-300 rounded-lg"
          >
            <option value="all">All Tasks</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
          <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {filteredTasks.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">No tasks found</h3>
          </div>
        ) : (
          filteredTasks.map((task) => (
            <div key={task.taskId} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <button
                    onClick={() => handleToggleStatus(task.taskId, task.status)}
                    className="mt-1"
                  >
                    {task.status === 'completed' ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <div className="w-6 h-6 border-2 border-slate-300 rounded-full hover:border-blue-600 transition-colors" />
                    )}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className={`font-semibold text-slate-800 ${task.status === 'completed' ? 'line-through text-slate-500' : ''}`}>
                        {task.title}
                      </h3>
                      {getPriorityBadge(task.priority)}
                    </div>
                    {task.description && (
                      <p className="text-slate-600 text-sm mb-3">{task.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      {task.clientName && (
                        <div className="flex items-center space-x-1">
                          <User className="w-4 h-4" />
                          <span>{task.clientName}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-1">
                        <span className="px-2 py-1 bg-slate-100 rounded text-xs">{task.taskType}</span>
                      </div>
                      {task.dueDate && (
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteTask(task.taskId)}
                  className="text-red-600 hover:text-red-700 p-2"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-bold mb-4">Create New Task</h3>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Client</label>
                <select
                  value={newTask.userId}
                  onChange={(e) => setNewTask({...newTask, userId: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select client</option>
                  {clients.map(c => (
                    <option key={c.principalId} value={c.principalId}>{c.name} (#{c.clientId})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows="3"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Type</label>
                  <select
                    value={newTask.taskType}
                    onChange={(e) => setNewTask({...newTask, taskType: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="follow_up">Follow Up</option>
                    <option value="call">Call</option>
                    <option value="email">Email</option>
                    <option value="meeting">Meeting</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Due Date</label>
                <input
                  type="datetime-local"
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Create Task
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentTasks;